
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@/lib/types';
import { ApiProvider } from './useApiProviders';
import { toast } from 'sonner';

export interface ApiIntegration {
  id: string;
  user_id: string;
  provider_id: string;
  access_token?: string;
  refresh_token?: string;
  token_expires_at?: string;
  credentials?: any;
  connection_status?: string;
  is_active?: boolean;
  last_synced_at?: string;
  created_at?: string;
  updated_at?: string;
  provider?: ApiProvider;
}

export interface ApiLog {
  id: string;
  user_id: string;
  provider_id: string;
  endpoint: string;
  status: string;
  error_message?: string;
  items_synced?: number;
  started_at?: string;
  completed_at?: string;
  provider?: ApiProvider;
}

export function useUserIntegrations(userId?: string) {
  const fetchUserIntegrations = async (): Promise<ApiIntegration[]> => {
    if (!userId) return [];
    
    const { data, error } = await supabase
      .from('user_api_integrations')
      .select('*, provider:api_providers(*)')
      .eq('user_id', userId);
    
    if (error) throw error;
    
    return (data || []).map(integration => ({
      ...integration,
      provider: integration.provider ? {
        ...integration.provider,
        auth_type: integration.provider.auth_type as 'oauth2' | 'api_key' | 'custom_token',
        endpoints: [] // Add empty endpoints array as default
      } : undefined
    }));
  };

  return useQuery({
    queryKey: ['user-integrations', userId],
    queryFn: fetchUserIntegrations,
    enabled: !!userId,
  });
}

export function useUserIntegration(providerId?: string, userId?: string) {
  const fetchUserIntegration = async (): Promise<ApiIntegration | null> => {
    if (!providerId || !userId) return null;
    
    const { data, error } = await supabase
      .from('user_api_integrations')
      .select('*, provider:api_providers(*)')
      .eq('provider_id', providerId)
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) throw error;
    
    if (!data) return null;
    
    return {
      ...data,
      provider: data.provider ? {
        ...data.provider,
        auth_type: data.provider.auth_type as 'oauth2' | 'api_key' | 'custom_token',
        endpoints: [] // Add empty endpoints array as default
      } : undefined
    };
  };

  return useQuery({
    queryKey: ['user-integration', providerId, userId],
    queryFn: fetchUserIntegration,
    enabled: !!providerId && !!userId,
  });
}

export function useCreateIntegration() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (integration: Omit<ApiIntegration, 'id'>) => {
      const { data, error } = await supabase
        .from('user_api_integrations')
        .insert(integration)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-integrations', data.user_id] });
      queryClient.invalidateQueries({ queryKey: ['user-integration', data.provider_id, data.user_id] });
      toast.success('API integration created successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to create API integration: ${error.message}`);
    },
  });
}

export function useUpdateIntegration() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, user_id, provider_id, ...updates }: Partial<ApiIntegration> & { id: string; user_id: string; provider_id: string }) => {
      const { data, error } = await supabase
        .from('user_api_integrations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return { ...data, user_id, provider_id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-integrations', data.user_id] });
      queryClient.invalidateQueries({ queryKey: ['user-integration', data.provider_id, data.user_id] });
      toast.success('API integration updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update API integration: ${error.message}`);
    },
  });
}

export function useDeleteIntegration() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, user_id, provider_id }: { id: string; user_id: string; provider_id: string }) => {
      const { error } = await supabase
        .from('user_api_integrations')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { id, user_id, provider_id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-integrations', data.user_id] });
      queryClient.invalidateQueries({ queryKey: ['user-integration', data.provider_id, data.user_id] });
      toast.success('API integration deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete API integration: ${error.message}`);
    },
  });
}

export function useApiLogs(userId?: string, limit = 100) {
  const fetchApiLogs = async (): Promise<ApiLog[]> => {
    if (!userId) return [];
    
    const { data, error } = await supabase
      .from('api_sync_logs')
      .select('*, provider:api_providers(*)')
      .eq('user_id', userId)
      .order('started_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    
    return (data || []).map(log => ({
      ...log,
      provider: log.provider ? {
        ...log.provider,
        auth_type: log.provider.auth_type as 'oauth2' | 'api_key' | 'custom_token',
        endpoints: [] // Add empty endpoints array as default
      } : undefined
    }));
  };

  return useQuery({
    queryKey: ['api-logs', userId, limit],
    queryFn: fetchApiLogs,
    enabled: !!userId,
  });
}
